import React, { useEffect, useState } from 'react';
import { LogIn } from 'lucide-react';
import { BTN } from '../styles/tokens';
import { fetchMainLoginUrl } from '../services/api/clientConfig';

// Phase C（SSO-only）：里嗨 AI 前台身份改由揪里嗨主網域 cvt 承載，已無本地帳密登入。
// 未帶有效 cvt（未登入 / 孤兒帳號）時，顯示此通知並導回主網域登入。

// 主網域（揪里嗨）登入網址由伺服器 /api/client-config 在執行期提供（各環境不同）。
// 取不到時按鈕停用並提示，不猜網域，以免 test 被導到正式站。

interface LoginPopupProps {
    isOpen: boolean;
}

const LoginPopup: React.FC<LoginPopupProps> = ({ isOpen }) => {
    const [loginUrl, setLoginUrl] = useState<string | null>(null);
    const [loadFailed, setLoadFailed] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        let alive = true;
        void fetchMainLoginUrl().then((url) => {
            if (!alive) return;
            setLoginUrl(url);
            setLoadFailed(url === null);
        });
        return () => { alive = false; };
    }, [isOpen]);

    if (!isOpen) return null;

    const goToMainLogin = () => {
        if (loginUrl) window.location.href = loginUrl;
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl w-full max-w-md mx-4 p-6 md:p-8 relative shadow-2xl animate-in fade-in zoom-in duration-300">
                <div className="text-center mb-6">
                    <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center">
                        <LogIn className="text-blue-600 dark:text-blue-400" size={26} />
                    </div>
                    <h2 className="text-xl md:text-2xl font-bold text-slate-900 dark:text-white mb-2">請先登入揪里嗨</h2>
                    <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed">
                        里嗨 AI 需透過揪里嗨帳號登入使用。<br />
                        請回到揪里嗨完成登入後再回來。
                    </p>
                </div>

                <button
                    type="button"
                    onClick={goToMainLogin}
                    disabled={!loginUrl}
                    className={`w-full ${BTN.primary} font-bold py-3 rounded-xl shadow-lg shadow-blue-600/20 disabled:opacity-60 disabled:cursor-not-allowed`}
                >
                    前往揪里嗨登入
                </button>
                {loadFailed && (
                    <p className="text-center text-xs text-red-500 mt-3">登入連結暫時無法取得，請重新整理頁面。</p>
                )}

                <p className="text-center text-xs text-slate-400 dark:text-slate-500 mt-5 leading-relaxed">
                    若已登入揪里嗨仍看到此畫面，代表尚未開通里嗨 AI 帳號，<br />
                    請聯絡客服協助開通。
                </p>
            </div>
        </div>
    );
};

export default LoginPopup;
